import React, {useMemo, useState} from 'react'
import {CopyText, Button, Modal, Icon} from '@therms/atalaya'
import {
  Alerts,
  CsvImportMapper,
  CsvImportSteps,
  useRPCContext,
} from '@therms/react-modules'
import {RPCDocs} from '@therms/rpc-gateway'
import {
  ItemColumnMapping,
  PersonColumnMapping,
  VehicleColumnMapping,
} from '@src/modules/Records/shared/BulkImportRecords/BulkImportRecordsColumnMapping.ts'
import {mapImportDataToRecordFields} from '@src/modules/Records/utils/mapImportDataToRecordFields.ts'
import {IoMdDownload} from 'react-icons/io'

type ExampleRow = Record<string, string>

const EXAMPLE_ROWS_BY_TYPE: Record<string, ExampleRow[]> = {
  [RPCDocs.Records.BulkCreateRecords.v1.Type.Item]: [
    {
      additional: 'Spare battery in case',
      brand: 'Motorola',
      color: 'Black',
      name: 'Handheld Radio',
      size: 'Medium',
      status: 'Active',
      style: 'Portable',
    },
  ],
  [RPCDocs.Records.BulkCreateRecords.v1.Type.Person]: [
    {
      additional: '',
      age: '34',
      alias: 'JD',
      criminalAuthority: '',
      criminalId: '',
      dob: '1991-04-12',
      email: 'jane.doe@example.com',
      eyes: 'Brown',
      gender: 'Female',
      hair: 'Brown',
      height: `5'6"`,
      idAuthority: 'MT',
      idNumber: 'D1234567',
      locality: 'Billings',
      name: 'Jane Doe',
      phone: '406-555-0100',
      race: 'White',
      region: 'MT',
      ss: '123-45-6789',
      streetAddress: '100 Main St',
      weight: '140',
    },
  ],
  [RPCDocs.Records.BulkCreateRecords.v1.Type.Vehicle]: [
    {
      additional: '',
      color: 'Black',
      make: 'Toyota',
      model: 'Camry',
      plate: '123ABC',
      state: 'MT',
      type: 'Car',
      vin: '12345ABCDE123',
      year: '2005',
    },
    {
      additional: '',
      color: 'Blue',
      make: 'Chevy',
      model: 'Traverse',
      plate: '456DEF',
      state: 'FL',
      type: 'SUV',
      vin: 'ABCDE12312345',
      year: '1996',
    },
    {
      additional: '',
      color: 'White',
      make: 'GMC',
      model: 'Sierra',
      plate: '1A2B3C',
      state: 'TX',
      type: 'Truck',
      vin: '12345123ABCDE',
      year: '2019',
    },
  ],
}

function escapeCsvCell(value: string) {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }

  return value
}

function downloadExampleCsv(
  filename: string,
  columns: {label: string; propKey: string}[],
  rows: ExampleRow[],
) {
  const csv = [
    columns.map((column) => escapeCsvCell(column.label)).join(','),
    ...rows.map((row) =>
      columns
        .map((column) => escapeCsvCell(row[column.propKey] ?? ''))
        .join(','),
    ),
  ].join('\n')

  const url = URL.createObjectURL(
    new Blob([csv], {type: 'text/csv;charset=utf-8'}),
  )
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

interface BulkImportRecordsProps {
  onComplete?: () => void
  onStepChange?: (step: CsvImportSteps) => void
  recordType:
    | RPCDocs.Records.CreateRecord.v1.Type
    | RPCDocs.Records.BulkCreateRecords.v1.Type
}

export function BulkImportRecords({
  onComplete,
  onStepChange,
  recordType,
}: BulkImportRecordsProps) {
  const {rpcService} = useRPCContext()
  const [step, setStep] = useState<CsvImportSteps>('pick-file')
  const [data, setData] = useState<
    RPCDocs.Records.BulkCreateRecords.v1.Fields[]
  >([])
  const [processing, setProcessing] = useState(false)
  const [processingErrored, setProcessingErrored] = useState<
    RPCDocs.Records.BulkCreateRecords.v1.Errored[]
  >([])
  const [skippedDuplicates, setSkippedDuplicates] = useState<number>(0)
  const [documentKey, setDocumentKey] = useState<string | undefined>()

  const handleOnComplete = (
    data: Array<Record<string, string | string[] | undefined>>,
  ) => {
    setData(data)
    setStep('info')
  }

  const runImport = async () => {
    let errors = 0

    const recordsToImport = mapImportDataToRecordFields({
      importData: data,
      recordType,
    })
    setProcessingErrored([])
    setSkippedDuplicates(0)
    setProcessing(true)

    try {
      await rpcService.Records.BulkCreateRecords.v1
        .call({
          documentKey,
          records: recordsToImport,
        })
        .then((response) => {
          if (response.data.errored.length) {
            setProcessingErrored(response.data.errored)
            errors++
            Alerts.error(
              `Import completed with ${response.data.errored.length} errored records. Please review the errors and fix the data before re-importing.`,
            )
          }

          if (response.data.skippedDuplicates > 0) {
            setSkippedDuplicates(response.data.skippedDuplicates)
            errors++
          }

          if (!errors && !skippedDuplicates && !processingErrored.length) {
            onComplete?.()

            Alerts.success(`Successfully imported ${data.length} records.`)
          }
        })
    } catch (e: any) {
      setProcessingErrored([e.message])
      console.error('Error processing row', e)
    }

    setProcessing(false)
  }

  const recordTypeMapping = useMemo(() => {
    if (recordType === RPCDocs.Records.BulkCreateRecords.v1.Type.Item) {
      return ItemColumnMapping
    }

    if (recordType === RPCDocs.Records.BulkCreateRecords.v1.Type.Person) {
      return PersonColumnMapping
    }

    if (recordType === RPCDocs.Records.BulkCreateRecords.v1.Type.Vehicle) {
      return VehicleColumnMapping
    }

    return []
  }, [recordType])

  if (step === 'pick-file')
    return (
      <div>
        <CsvImportMapper
          allowInlineEditing={false}
          columnMapping={recordTypeMapping}
          onComplete={handleOnComplete}
          onStepChange={onStepChange}
          setDocumentKey={setDocumentKey}
        />

        <div className="pt-sm flex w-full justify-center">
          <Button
            onClick={() =>
              downloadExampleCsv(
                `${recordType}-example.csv`,
                recordTypeMapping,
                EXAMPLE_ROWS_BY_TYPE[recordType] ?? [{}],
              )
            }
            size="xs"
            subtle
          >
            <Icon Icon={IoMdDownload} size="sm" />
            <span className="pl-xxs">Download Example CSV</span>
          </Button>
        </div>
      </div>
    )

  if (step === 'info') {
    return (
      <div>
        <div className="flex justify-center">
          <Button
            className="mt-base"
            disabled={processing}
            onClick={() => {
              setStep('process')
              runImport()
            }}
            variant="main"
          >
            Run Import
          </Button>
        </div>
      </div>
    )
  }

  if (step === 'process') {
    return (
      <div className="flex w-full justify-center text-center">
        {processing ? <div>Importing {data.length} Records...</div> : null}

        {!processing &&
        (processingErrored?.length > 0 || skippedDuplicates > 0) ? (
          <div className="">
            <p>Processed {data.length} Records</p>

            {processingErrored.length > 0 && (
              <>
                <h3 className="text-critical">
                  Errors {processingErrored.length}
                </h3>
                <div>
                  <CopyText value={JSON.stringify(processingErrored)}>
                    Copy Errors to Clipboard
                  </CopyText>
                </div>

                <ul>
                  {processingErrored.map((error, index) => (
                    <>
                      {error.lineNumber ? (
                        <li key={index}>
                          Row {error.lineNumber}: {error.error}
                          <pre>{JSON.stringify(error.error, null, 2)}</pre>
                        </li>
                      ) : (
                        <pre>{JSON.stringify(error, null, 2)}</pre>
                      )}
                    </>
                  ))}
                </ul>
              </>
            )}

            {skippedDuplicates > 0 && (
              <h3 className="text-critical">
                {skippedDuplicates} Duplicate Records skipped
              </h3>
            )}
          </div>
        ) : (
          <div>
            <p className="text-info">Records have been processed.</p>
          </div>
        )}
      </div>
    )
  }
}

export function BulkImportRecordsModal({
  closeHandler,
  onComplete,
  ...props
}: {
  closeHandler: () => void
} & BulkImportRecordsProps) {
  const [modalWidth, setModalWidth] = useState('sm')

  return (
    <Modal
      closeHandler={() => closeHandler()}
      header="Import Records"
      width={modalWidth}
    >
      <BulkImportRecords
        onStepChange={(step) => {
          if (step === 'pick-file') setModalWidth('sm')
          if (step === 'select-mapping') setModalWidth('md')
          if (step === 'finalize') setModalWidth('xl')
          if (step === 'info') setModalWidth('sm')
        }}
        onComplete={() => {
          onComplete?.()
          closeHandler()
        }}
        {...props}
      />
    </Modal>
  )
}
